<script>
import Modal from "@/components/modals/modal-form.vue";
import api from '@/helpers/apirest/api';
import Status from '@/helpers/general/status';
import Prism from 'prismjs';
import Vue from 'vue';
import 'prismjs/components/prism-json';
import 'prismjs/themes/prism.css';

export default {
  props: {
    modalId: String,
    id: Number
  },
  data() {
    return {
      syncLog: null,
      isVisible: false,
      isRetrying: false
    };
  },
  components: {
    Modal
  },
  emits: ['retried'],
  computed: {
    modalTitle() {
      return this.syncLog?.sync_id 
        ? `Detalle de Sincronización - ${this.syncLog.sync_id}` 
        : 'Detalle de Sincronización';
    },
    entityTypeName() {
      if (!this.syncLog?.entity_type) return 'N/A';
      return this.syncLog.entity_type.split('\\').pop();
    },
    hasError() {
      return this.syncLog?.error_message || this.syncLog?.error_trace;
    },
    canRetry() {
      return this.syncLog?.status === 'failed'
        && this.syncLog?.direction === 'outbound'
        && !this.syncLog?.is_from_webhook;
    }
  },
  methods: {
    async shown() {
      this.isVisible = true;
      if (this.id) {
        await this.loadDetail();
      }
    },
    async loadDetail() {
      const response = await api.get({
        url: `sync-logs/${this.id}`,
        config: {
          withLoading: true
        }
      });

      this.syncLog = response?.data?.data;
      
      // Wait for DOM update before highlighting
      this.$nextTick(() => {
        this.highlightAllCode();
      });
    },
    highlightAllCode() {
      if (this.$refs.payload) {
        Prism.highlightElement(this.$refs.payload);
      }
      if (this.$refs.response) {
        Prism.highlightElement(this.$refs.response);
      }
      if (this.$refs.metadata) {
        Prism.highlightElement(this.$refs.metadata);
      }
    },
    cancel() {
      this.$bvModal.hide(this.modalId);
    },
    resetModal() {
      this.syncLog = null;
      this.isVisible = false;
    },
    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      return d.toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    },
    getStatusBadgeClass(status) {
      const statusItem = Status.getList('sync_log_status').find(item => item.id === status);
      return statusItem?.class?.replace('badge ', '') || 'badge-soft-secondary';
    },
    getStatusLabel(status) {
      return Status.getStatus({ itemStatus: status, type: 'sync_log_status', html: false });
    },
    getDirectionBadgeClass(direction) {
      const directionItem = Status.getList('sync_log_direction').find(item => item.id === direction);
      return directionItem?.class?.replace('badge ', '') || 'badge-soft-secondary';
    },
    getDirectionLabel(direction) {
      return Status.getStatus({ itemStatus: direction, type: 'sync_log_direction', html: false });
    },
    getOperationBadgeClass(operation) {
      const operationItem = Status.getList('sync_log_operation').find(item => item.id === operation);
      return operationItem?.class?.replace('badge ', '') || 'badge-soft-secondary';
    },
    getOperationLabel(operation) {
      return Status.getStatus({ itemStatus: operation, type: 'sync_log_operation', html: false });
    },
    formatJSON(obj) {
      if (!obj) return 'null';
      return JSON.stringify(obj, null, 2);
    },
    async retrySync() {
      if (!this.canRetry || this.isRetrying) {
        return;
      }

      try {
        const result = await Vue.swal({
          title: 'Reintentar sincronización',
          text: 'Se volverá a encolar el envío a Zoho con los datos actuales de la entidad.',
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Reintentar',
          cancelButtonText: 'Cancelar',
        });

        if (!result.isConfirmed) {
          return;
        }

        this.isRetrying = true;

        await api.post({
          url: `sync-logs/${this.id}/retry`,
          config: {
            withLoading: true,
          },
        });

        this.$bvToast.toast('Reintento de sincronización encolado correctamente', {
          title: 'Sincronización',
          variant: 'success',
          solid: true,
        });

        await this.loadDetail();
        this.$emit('retried');
      } catch (error) {
        const message = error?.response?.data?.errors?.[0]?.title || 'No se pudo reintentar la sincronización';
        this.$bvToast.toast(message, {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.isRetrying = false;
      }
    }
  }
};
</script>

<template>
  <div>
    <Modal 
      :title="modalTitle" 
      :id="modalId" 
      @cancel="cancel" 
      @shown="shown" 
      @hidden="resetModal"
      :size="'extra-xl'" 
      :showConfirmButton="false" 
      :label-cancel-btn="'Cerrar'"
    >
      <div v-if="syncLog">
        <b-tabs content-class="p-3" nav-class="nav-tabs-custom">
          <!-- Tab 1: General Information -->
          <b-tab active>
            <template v-slot:title>
              <i class="mdi mdi-information-outline mr-1"></i>
              <span>Información General</span>
            </template>
            
            <div class="row">
              <div class="col-md-6 mb-3">
                <div class="card">
                  <div class="card-body">
                    <h5 class="card-title mb-3">
                      <i class="mdi mdi-database mr-2"></i>Identificación
                    </h5>
                    <div class="table-responsive">
                      <table class="table table-sm table-borderless mb-0">
                        <tbody>
                          <tr>
                            <td class="text-muted" style="width: 40%"><strong>ID:</strong></td>
                            <td>{{ syncLog.id }}</td>
                          </tr>
                          <tr>
                            <td class="text-muted"><strong>Sync ID:</strong></td>
                            <td><code>{{ syncLog.sync_id }}</code></td>
                          </tr>
                          <tr>
                            <td class="text-muted"><strong>Entidad:</strong></td>
                            <td>
                              {{ entityTypeName }} #{{ syncLog.entity_id }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="card">
                  <div class="card-body">
                    <h5 class="card-title mb-3">
                      <i class="mdi mdi-cog mr-2"></i>Operación
                    </h5>
                    <div class="table-responsive">
                      <table class="table table-sm table-borderless mb-0">
                        <tbody>
                          <tr>
                            <td class="text-muted" style="width: 40%"><strong>Dirección:</strong></td>
                            <td>
                              <span 
                                class="badge" 
                                :class="getDirectionBadgeClass(syncLog.direction)"
                              >
                                {{ getDirectionLabel(syncLog.direction) }}
                              </span>
                            </td>
                          </tr>
                          <tr>
                            <td class="text-muted"><strong>Operación:</strong></td>
                            <td>
                              <span 
                                class="badge" 
                                :class="getOperationBadgeClass(syncLog.operation)"
                              >
                                {{ getOperationLabel(syncLog.operation) }}
                              </span>
                            </td>
                          </tr>
                          <tr>
                            <td class="text-muted"><strong>Estado:</strong></td>
                            <td>
                              <span 
                                class="badge" 
                                :class="getStatusBadgeClass(syncLog.status)"
                              >
                                {{ getStatusLabel(syncLog.status) }}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="card">
                  <div class="card-body">
                    <h5 class="card-title mb-3">
                      <i class="mdi mdi-cloud mr-2"></i>Zoho
                    </h5>
                    <div class="table-responsive">
                      <table class="table table-sm table-borderless mb-0">
                        <tbody>
                          <tr>
                            <td class="text-muted" style="width: 40%"><strong>Módulo:</strong></td>
                            <td>{{ syncLog.zoho_module || '-' }}</td>
                          </tr>
                          <tr>
                            <td class="text-muted"><strong>Zoho ID:</strong></td>
                            <td>
                              <code v-if="syncLog.zoho_id">{{ syncLog.zoho_id }}</code>
                              <span v-else>-</span>
                            </td>
                          </tr>
                          <tr>
                            <td class="text-muted"><strong>Webhook:</strong></td>
                            <td>
                              <span class="badge" :class="syncLog.is_from_webhook ? 'badge-soft-success' : 'badge-soft-secondary'">
                                {{ syncLog.is_from_webhook ? 'Sí' : 'No' }}
                              </span>
                              <span v-if="syncLog.webhook_id" class="ml-2 text-muted">
                                (ID: {{ syncLog.webhook_id }})
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="card">
                  <div class="card-body">
                    <h5 class="card-title mb-3">
                      <i class="mdi mdi-clock-outline mr-2"></i>Procesamiento
                    </h5>
                    <div class="table-responsive">
                      <table class="table table-sm table-borderless mb-0">
                        <tbody>
                          <tr>
                            <td class="text-muted" style="width: 40%"><strong>Intentos:</strong></td>
                            <td>
                              <span class="badge badge-soft-info">
                                {{ syncLog.attempts }} / {{ syncLog.max_attempts }}
                              </span>
                            </td>
                          </tr>
                          <tr>
                            <td class="text-muted"><strong>Programado:</strong></td>
                            <td>{{ formatDate(syncLog.scheduled_at) }}</td>
                          </tr>
                          <tr>
                            <td class="text-muted"><strong>Procesado:</strong></td>
                            <td>{{ formatDate(syncLog.processed_at) }}</td>
                          </tr>
                          <tr>
                            <td class="text-muted"><strong>Completado:</strong></td>
                            <td>{{ formatDate(syncLog.completed_at) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </b-tab>

          <!-- Tab 2: Payload -->
          <b-tab v-if="syncLog.payload">
            <template v-slot:title>
              <i class="mdi mdi-upload mr-1"></i>
              <span>Payload Enviado</span>
            </template>
            
            <div class="card">
              <div class="card-body">
                <h5 class="card-title mb-3">
                  <i class="mdi mdi-code-json mr-2"></i>Datos Enviados a Zoho
                </h5>
                <pre class="language-json" style="max-height: 600px; overflow-y: auto;">
                  <code ref="payload">{{ formatJSON(syncLog.payload) }}</code>
                </pre>
              </div>
            </div>
          </b-tab>

          <!-- Tab 3: Response -->
          <b-tab v-if="syncLog.response">
            <template v-slot:title>
              <i class="mdi mdi-download mr-1"></i>
              <span>Respuesta de Zoho</span>
            </template>
            
            <div class="card">
              <div class="card-body">
                <h5 class="card-title mb-3">
                  <i class="mdi mdi-code-json mr-2"></i>Respuesta Recibida de Zoho
                </h5>
                <pre class="language-json" style="max-height: 600px; overflow-y: auto;">
                  <code ref="response">{{ formatJSON(syncLog.response) }}</code>
                </pre>
              </div>
            </div>
          </b-tab>

          <!-- Tab 4: Error (Only if there's an error) -->
          <b-tab v-if="hasError">
            <template v-slot:title>
              <i class="mdi mdi-alert-circle-outline mr-1 text-danger"></i>
              <span>Error</span>
            </template>
            
            <div class="card border-danger">
              <div class="card-body">
                <h5 class="card-title text-danger mb-3">
                  <i class="mdi mdi-alert mr-2"></i>Información de Error
                </h5>
                
                <div v-if="syncLog.error_message" class="mb-4">
                  <h6 class="text-muted mb-2">Mensaje de Error:</h6>
                  <div class="alert alert-danger" role="alert">
                    {{ syncLog.error_message }}
                  </div>
                </div>

                <div v-if="syncLog.error_trace">
                  <h6 class="text-muted mb-2">Stack Trace:</h6>
                  <b-card no-body class="shadow-none">
                    <b-card-header header-tag="header" role="tab">
                      <a
                        v-b-toggle.error-trace-collapse
                        class="text-dark"
                        href="javascript: void(0);"
                      >
                        <i class="mdi mdi-chevron-down mr-1"></i>
                        Ver Stack Trace Completo
                      </a>
                    </b-card-header>
                    <b-collapse id="error-trace-collapse" role="tabpanel">
                      <b-card-body>
                        <pre class="bg-light p-3" style="max-height: 400px; overflow-y: auto; font-size: 12px;">{{ syncLog.error_trace }}</pre>
                      </b-card-body>
                    </b-collapse>
                  </b-card>
                </div>

                <div v-if="canRetry" class="mt-4">
                  <b-button
                    variant="warning"
                    size="sm"
                    :disabled="isRetrying"
                    @click="retrySync"
                  >
                    <b-spinner v-if="isRetrying" small class="mr-1"></b-spinner>
                    <i v-else class="mdi mdi-refresh mr-1"></i>
                    Reintentar sincronización
                  </b-button>
                </div>
              </div>
            </div>
          </b-tab>

          <!-- Tab 5: Metadata (Optional) -->
          <b-tab v-if="syncLog.metadata && Object.keys(syncLog.metadata).length > 0">
            <template v-slot:title>
              <i class="mdi mdi-information mr-1"></i>
              <span>Metadata</span>
            </template>
            
            <div class="card">
              <div class="card-body">
                <h5 class="card-title mb-3">
                  <i class="mdi mdi-code-json mr-2"></i>Información Adicional
                </h5>
                <pre class="language-json" style="max-height: 600px; overflow-y: auto;">
                  <code ref="metadata">{{ formatJSON(syncLog.metadata) }}</code>
                </pre>
              </div>
            </div>
          </b-tab>

          <!-- Tab 6: Entity (Optional - if entity data is available) -->
          <b-tab v-if="syncLog.entity && Object.keys(syncLog.entity).length > 1">
            <template v-slot:title>
              <i class="mdi mdi-database-outline mr-1"></i>
              <span>Datos de la Entidad</span>
            </template>
            
            <div class="card">
              <div class="card-body">
                <h5 class="card-title mb-3">
                  <i class="mdi mdi-file-document-outline mr-2"></i>Información de {{ entityTypeName }}
                </h5>
                <div class="table-responsive">
                  <table class="table table-bordered">
                    <thead class="bg-light">
                      <tr>
                        <th style="width: 30%">Campo</th>
                        <th>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(value, key) in syncLog.entity" :key="key">
                        <td class="text-muted"><strong>{{ key }}</strong></td>
                        <td>
                          <span v-if="typeof value === 'object'">
                            <code>{{ JSON.stringify(value) }}</code>
                          </span>
                          <span v-else>{{ value }}</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </b-tab>
        </b-tabs>
      </div>

      <div v-else class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Cargando...</span>
        </div>
      </div>
    </Modal>
  </div>
</template>

<style scoped>
pre {
  border-radius: 4px;
  background-color: #f5f5f5;
}

code {
  font-family: 'Courier New', Courier, monospace;
  font-size: 13px;
}

.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.card-title {
  color: #495057;
  font-weight: 600;
}

.table td {
  padding: 0.5rem;
  vertical-align: middle;
}
</style>
